import React, { useCallback, useEffect, useState } from 'react';

import { EmptyState, ErrorState, ListaSelecao, Modal, Pagination, Skeleton } from '../../../ui';

import { fetchSgaCategorias, Categoria } from '../../../api/sga-categoria/sga-categoriaApi';
import { extrairMensagemErro } from '../../../utils/apiError';

/**
 * Escolha da categoria do veículo (reescrito na at-59 sobre o `Modal` do kit).
 *
 * ## O que a reescrita mudou
 *
 *   1. **Visual do design system**, lista alcançável pelo teclado (era
 *      `<li onClick>`) e paginação em `<button>` (era `<div onClick>`).
 *   2. **Falha deixou de ser muda** — era toast + lista vazia.
 *   3. **O vazio explica o caso mais comum**: a categoria depende do TIPO, e há
 *      tipo que simplesmente não tem categoria no SGA — o **35 (motocicleta
 *      elétrica)** é justamente um deles (at-46). Antes o modal abria com um
 *      "Nenhuma categoria encontrada." seco, que se lia como falha.
 *
 * ⚠️ **As props não mudaram** (`isOpen`/`onClose`/`onSelectCategoria`/
 * `codigoTipo`). A lista é filtrada pelo tipo, e quem garante que ele existe é o
 * `FormularioVeiculo` — que avisa antes de abrir se nenhum tipo foi escolhido.
 */

interface ModalCategoriasProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCategoria: (categoria: Categoria) => void;
  codigoTipo: string;
}

type Estado = 'carregando' | 'ok' | 'erro';

const POR_PAGINA = 10;

const ModalCategorias: React.FC<ModalCategoriasProps> = ({
  isOpen,
  onClose,
  onSelectCategoria,
  codigoTipo,
}) => {
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [total, setTotal] = useState(0);
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState('');
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    if (isOpen) setPagina(1);
  }, [isOpen]);

  const buscar = useCallback(async () => {
    setEstado('carregando');
    try {
      const resposta = await fetchSgaCategorias(codigoTipo, pagina, POR_PAGINA);

      if (!resposta || !Array.isArray(resposta.items)) {
        setErro('A resposta do servidor não trouxe a lista de categorias.');
        setEstado('erro');
        return;
      }

      setCategorias(resposta.items);
      setTotal(resposta.total ?? 0);
      setEstado('ok');
    } catch (falha) {
      setErro(extrairMensagemErro(falha, 'Não foi possível carregar as categorias.'));
      setEstado('erro');
    }
  }, [codigoTipo, pagina]);

  useEffect(() => {
    if (isOpen) void buscar();
  }, [isOpen, buscar]);

  return (
    <Modal
      aberto={isOpen}
      titulo="Selecionar Categoria"
      tamanho="sm"
      aoFechar={onClose}
      rodape={
        estado === 'ok' && categorias.length > 0 ? (
          <Pagination
            pagina={pagina}
            totalItens={total}
            porPagina={POR_PAGINA}
            aoTrocar={setPagina}
          />
        ) : undefined
      }
    >
      {estado === 'carregando' && <Skeleton linhas={4} altura="tw-h-11" />}

      {estado === 'erro' && <ErrorState mensagem={erro} aoTentarNovamente={() => void buscar()} />}

      {estado === 'ok' && (
        <ListaSelecao
          itens={categorias}
          chave={(c) => c.codigo_categoria}
          rotulo={(c) => c.descricao_categoria}
          aoEscolher={(c) => {
            onSelectCategoria(c);
            onClose();
          }}
          rotuloAcessivel="Categorias do tipo de veículo escolhido"
          vazio={
            <EmptyState
              icone="sem-dados"
              titulo="Este tipo de veículo não tem categoria ativa no SGA"
              descricao="O SGA recusa o cadastro de veículo deste tipo enquanto ele não tiver categoria. Escolha outro tipo de veículo ou peça à associação para ativar uma categoria para ele. (Motocicleta elétrica é a exceção: nela a categoria é opcional.)"
            />
          }
        />
      )}
    </Modal>
  );
};

export default ModalCategorias;
